
import { defineComponent, provide } from "vue";
import { mapGetters } from "vuex";
import * as bs from "bootstrap";
import OrgTreeNode from "@/components/binary-tree/OrgTreeNode.vue";
import BinaryTreeSkeleton from "@/components/binary-tree/BinaryTreeSkeleton.vue";
import BinaryTreeEmpty from "@/components/binary-tree/BinaryTreeEmpty.vue";
import { errorModal } from "@/components/modals";
import {
  loadSubtree,
  setRoot,
  expandSubtree,
  loadViewState,
  saveViewState,
  loadNodesBatch,
  SET_HOME,
  SET_FOCUS,
  CLEAR_EXPANDED,
  APPLY_VIEW_STATE,
  BinaryTreeViewStatus,
} from "@/store/BinaryTree/BinaryTreeInterfaces";

// Cap how many open nodes we restore in one shot, so a pathologically large
// saved state can't freeze the first render. Excess branches stay collapsed.
const MAX_RESTORED_OPEN = 400;
const SAVE_DEBOUNCE_MS = 1500;
const FOCUS_COUNT = 3;
const STALE_MS = 30 * 24 * 60 * 60 * 1000; // ignore expansion older than 30 days
// Minimap: max on-screen size (px); the schematic is scaled to fit within this.
const MM_MAX_W = 200;
const MM_MAX_H = 150;
const MM_DEBOUNCE_MS = 180;

// Shared viewport observer that loads a card's profile image only after it has
// dwelled in view, so scrolling a big tree doesn't fire thousands of downloads.
function createCardObserver() {
  let observer: IntersectionObserver | null = null;
  const pending = new Set<Element>();
  const onDwellMap = new Map<Element, () => void>();
  const timers = new Map<Element, number>();
  const done = new WeakSet<Element>();
  const DWELL_MS = 700;

  const handle = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      const el = entry.target;
      if (entry.isIntersecting) {
        if (done.has(el) || timers.has(el)) return;
        const id = window.setTimeout(() => {
          timers.delete(el);
          if (done.has(el)) return;
          done.add(el);
          if (observer) observer.unobserve(el);
          const cb = onDwellMap.get(el);
          onDwellMap.delete(el);
          if (cb) cb();
        }, DWELL_MS);
        timers.set(el, id);
      } else {
        const id = timers.get(el);
        if (id !== undefined) {
          window.clearTimeout(id);
          timers.delete(el);
        }
      }
    });
  };

  return {
    init(root: Element) {
      if (observer) return;
      observer = new IntersectionObserver(handle, {
        root,
        rootMargin: "200px",
      });
      pending.forEach((el) => observer && observer.observe(el));
      pending.clear();
    },
    register(el: Element, onDwell: () => void) {
      if (done.has(el)) return;
      onDwellMap.set(el, onDwell);
      if (observer) observer.observe(el);
      else pending.add(el);
    },
    unregister(el: Element) {
      const id = timers.get(el);
      if (id !== undefined) {
        window.clearTimeout(id);
        timers.delete(el);
      }
      onDwellMap.delete(el);
      pending.delete(el);
      if (observer) observer.unobserve(el);
    },
    destroy() {
      if (observer) observer.disconnect();
      observer = null;
      timers.forEach((id) => window.clearTimeout(id));
      timers.clear();
      onDwellMap.clear();
      pending.clear();
    },
  };
}

export default defineComponent({
  name: "binary-tree",
  components: { OrgTreeNode, BinaryTreeSkeleton, BinaryTreeEmpty, errorModal },
  data() {
    return {
      searchInput: "",
      expandDepth: 2,
      // Levels pulled for the root on first load (reveals initialDepth - 1).
      initialDepth: 3,
      // Arranca en true: entre el mount y que la restauración async dispare la
      // primera carga hay una ventana en la que no hay loading NI root; con la
      // red lenta eso pintaba el empty state ("no tienes posición") en vez del
      // skeleton. Con true, se ve el skeleton hasta que la carga resuelve.
      loading: true,
      error: "",
      // Zoom del lienzo (task 4): scale sobre .tp-content; applyZoom ajusta el
      // tamaño de .tp-inner al tamaño visual (natural * zoom) para que el área de
      // scroll siga al árbol escalado (paneo) y "Ajustar" lo encuadre completo.
      zoom: 1,
      // ErrorModal genérico reutilizado para fallos reales de carga/red. El
      // "usuario no encontrado" de la búsqueda sigue siendo el hint inline `error`.
      errorModalInst: null as any,
      errorTitle: "",
      errorMessage: "",
      notice: "",
      restoring: false,
      saveTimer: 0,
      // Minimap overview state. `boxes` are scaled node rects; `vp` is the
      // viewport rectangle. Recomputed (debounced) on tree changes/resize;
      // `vp` updates cheaply on scroll.
      minimap: {
        show: false,
        w: 0,
        h: 0,
        scale: 1,
        boxes: [] as Array<{ x: number; y: number; w: number; h: number }>,
        vp: { x: 0, y: 0, w: 0, h: 0 },
      },
      minimapHidden: false,
      mmRaf: 0,
      mmTimer: 0,
      mmDragging: false,
      // Reposicionar el minimapa arrastrando su manija. SIN persistencia: se
      // resetea al recargar (las resoluciones cambian entre sesiones, así que
      // guardar la posición no aporta). Independiente del drag de navegación.
      mmPosLeft: null as number | null,
      mmPosTop: null as number | null,
      mmMoveActive: false,
      mmMovePointerId: null as number | null,
      mmMoveOffX: 0,
      mmMoveOffY: 0,
      mmMoveStageL: 0,
      mmMoveStageT: 0,
      mmMoveStageW: 0,
      mmMoveStageH: 0,
      mmMoveW: 0,
      mmMoveH: 0,
      // Last content size the schematic was built for, so scrolling into a
      // lazily-loaded branch (which grows the content) triggers a rebuild.
      mmContentW: 0,
      mmContentH: 0,
      // Drag-to-pan (mouse/lápiz) sobre el lienzo, además del scroll/minimapa.
      // En touch se deja el scroll nativo del contenedor.
      panning: false,
      panPointerId: null as number | null,
      panStartX: 0,
      panStartY: 0,
      panStartLeft: 0,
      panStartTop: 0,
      suppressClick: false,
    };
  },
  provide() {
    return {
      // Injected by every OrgTreeNode so it can re-root the chart on a user
      // without drilling props/emits through the recursion. Keeps the home root.
      binaryTreeReroot: (username: string): void => {
        this.navigateTo(username);
      },
    };
  },
  computed: {
    ...mapGetters("binaryTree", [
      "nodeFor",
      "treeRoot",
      "homeRoot",
      "isExpanded",
    ]),
    zoomStyle(): Record<string, string> {
      return {
        transform: `scale(${this.zoom})`,
        transformOrigin: "0 0",
      };
    },
    minimapStyle(): Record<string, string> {
      const s: Record<string, string> = {
        width: this.minimap.w + "px",
        height: this.minimap.h + "px",
      };
      // Una vez arrastrado, fija left/top y anula el right/bottom por defecto.
      if (this.mmPosLeft !== null && this.mmPosTop !== null) {
        s.left = this.mmPosLeft + "px";
        s.top = this.mmPosTop + "px";
        s.right = "auto";
        s.bottom = "auto";
      }
      return s;
    },
    rootUsername(): string {
      return this.treeRoot;
    },
    rootReady(): boolean {
      return !!this.nodeFor(this.rootUsername);
    },
    atHome(): boolean {
      return !this.rootUsername || this.rootUsername === this.homeRoot;
    },
    currentUser(): string {
      return (
        (this.$store.state as any).auth?.userProfileData?._usernamePK || ""
      );
    },
    storeRevision(): number {
      return (this.$store.state as any).binaryTree.revision;
    },
    storeFocus(): string[] {
      return (this.$store.state as any).binaryTree.focusUsernames || [];
    },
  },
  methods: {
    showError(title: string, message: string): void {
      this.errorTitle = title;
      this.errorMessage = message;
      if (this.errorModalInst && this.errorModalInst.show) {
        this.errorModalInst.show();
      }
    },
    zoomIn(): void {
      this.zoom = Math.min(2, Math.round((this.zoom + 0.1) * 100) / 100);
    },
    zoomOut(): void {
      this.zoom = Math.max(0.3, Math.round((this.zoom - 0.1) * 100) / 100);
    },
    zoomFit(): void {
      const canvas = this.$refs.canvas as HTMLElement | undefined;
      const content = this.$refs.content as HTMLElement | undefined;
      if (!canvas || !content) return;
      // offsetWidth/Height son el tamaño de LAYOUT (transform:scale no los altera),
      // así que dan el tamaño natural del árbol sea cual sea el zoom actual.
      const natW = content.offsetWidth;
      const natH = content.offsetHeight;
      if (!natW || !natH) return;
      const s = Math.min(
        canvas.clientWidth / natW,
        canvas.clientHeight / natH,
        1
      );
      this.zoom = Math.max(0.3, Math.round(s * 100) / 100);
      this.$nextTick(() => {
        this.applyZoom();
        this.centerScroll();
      });
    },
    // Ajusta .tp-inner (contenido con scroll) al tamaño VISUAL del árbol. Como
    // .tp-content es absolute + transform:scale, su caja de layout no aporta al
    // scroll; aquí fijamos ancho/alto = natural*zoom y centramos en horizontal
    // cuando el árbol es más angosto que el lienzo.
    applyZoom(): void {
      const canvas = this.$refs.canvas as HTMLElement | undefined;
      const inner = this.$refs.inner as HTMLElement | undefined;
      const content = this.$refs.content as HTMLElement | undefined;
      if (!canvas || !inner || !content) return;
      const natW = content.offsetWidth;
      const natH = content.offsetHeight;
      const scaledW = natW * this.zoom;
      const scaledH = natH * this.zoom;
      const left = Math.max(0, (canvas.clientWidth - scaledW) / 2);
      content.style.left = left + "px";
      inner.style.width = Math.ceil(left + scaledW) + "px";
      inner.style.height = Math.ceil(scaledH) + "px";
    },
    // Drag-to-pan sobre el lienzo (además del scroll y el minimapa). Mouse/lápiz:
    // arrastrar con el botón izquierdo panea. En touch se deja el scroll nativo
    // del contenedor (que ya panea con el dedo) para no pelear con él. Un umbral
    // distingue "clic en un nodo" de "arrastre": si el puntero se movió, se panea
    // y se traga el clic siguiente para no togglear el nodo bajo el cursor.
    onCanvasPointerDown(e: PointerEvent): void {
      // Resetea la supresión al empezar cualquier interacción: si un browser no
      // dispara el click tras capturar el puntero (Safari/Firefox difieren de
      // Chrome), suppressClick no debe quedar pegado y tragar el próximo clic.
      this.suppressClick = false;
      if (e.pointerType === "touch") return; // touch: scroll nativo del contenedor
      if (e.pointerType === "mouse" && e.button !== 0) return;
      const canvas = this.$refs.canvas as HTMLElement | undefined;
      if (!canvas) return;
      this.panPointerId = e.pointerId;
      this.panStartX = e.clientX;
      this.panStartY = e.clientY;
      this.panStartLeft = canvas.scrollLeft;
      this.panStartTop = canvas.scrollTop;
      this.panning = false;
    },
    onCanvasPointerMove(e: PointerEvent): void {
      if (this.panPointerId === null || e.pointerId !== this.panPointerId)
        return;
      const canvas = this.$refs.canvas as HTMLElement | undefined;
      if (!canvas) return;
      const dx = e.clientX - this.panStartX;
      const dy = e.clientY - this.panStartY;
      if (!this.panning) {
        // Umbral: por debajo de 5px sigue pudiendo ser un clic en el nodo.
        if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
        this.panning = true;
        canvas.classList.add("tp-panning");
        try {
          canvas.setPointerCapture(this.panPointerId);
        } catch (err) {
          // captura best-effort
        }
      }
      canvas.scrollLeft = this.panStartLeft - dx;
      canvas.scrollTop = this.panStartTop - dy;
    },
    onCanvasPointerUp(e: PointerEvent): void {
      if (this.panPointerId === null || e.pointerId !== this.panPointerId)
        return;
      const canvas = this.$refs.canvas as HTMLElement | undefined;
      if (canvas) {
        canvas.classList.remove("tp-panning");
        if (canvas.hasPointerCapture && canvas.hasPointerCapture(e.pointerId)) {
          canvas.releasePointerCapture(e.pointerId);
        }
      }
      // Si hubo arrastre, traga el clic siguiente para no abrir/cerrar el nodo.
      this.suppressClick = this.panning;
      this.panning = false;
      this.panPointerId = null;
    },
    onCanvasClickCapture(e: MouseEvent): void {
      if (this.suppressClick) {
        e.stopPropagation();
        e.preventDefault();
        this.suppressClick = false;
      }
    },
    // Core loader. asHome=true makes `username` the entry-point root that
    // "Ir a la raíz" returns to; in-tree navigation passes false to keep it.
    async goToRoot(username: string, asHome: boolean): Promise<void> {
      username = (username || "").trim().toLowerCase();
      if (!username) return;
      this.loading = true;
      this.error = "";
      try {
        const resolved: string = await this.$store.dispatch(
          "binaryTree/" + setRoot,
          username
        );
        if (asHome) {
          this.$store.commit("binaryTree/" + SET_HOME, resolved);
        }
        await this.$store.dispatch("binaryTree/" + loadSubtree, {
          username: resolved,
          levels: this.initialDepth,
        });
        // Seed the initial reveal only for a root not expanded yet, so returning
        // to an already-open tree keeps exactly what was expanded.
        if (!this.isExpanded(resolved)) {
          this.$store.dispatch("binaryTree/" + expandSubtree, {
            username: resolved,
            levels: this.initialDepth - 1,
          });
        }
        this.$nextTick(() => this.centerScroll());
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        const name = error instanceof Error ? error.name : "";
        if (name === "NOT_PLACED" || /NOT_PLACED/i.test(msg)) {
          // Estado ESPERADO, no un fallo: el usuario existe pero aún no tiene
          // nodo binario (dormidos post-reset, o nuevos antes de la primera
          // activación, donde ocurre la colocación — §17). El backend lanza
          // `NOT_PLACED` verbatim (UsersFunc). rootReady queda en false, así que
          // se limpia `error` y se deja que renderice SOLO el empty state
          // (BinaryTreeEmpty), sin el modal engañoso de "problema al cargar".
          this.error = "";
        } else if (/not\s*found|encontrad|USER_NOT_FOUND|autoriz/i.test(msg)) {
          // "usuario no encontrado" → hint inline suave junto al buscador.
          this.error = "Ese usuario no existe o no pertenece a tu red.";
        } else {
          // Fallo real de carga/red → ErrorModal genérico reutilizado.
          this.error = "";
          this.showError(
            "No se pudo cargar la red",
            "Ocurrió un problema al cargar el árbol binario. Revisa tu conexión e inténtalo de nuevo."
          );
        }
      } finally {
        this.loading = false;
      }
    },
    load(username: string): Promise<void> {
      // Search / initial load establish a new home root.
      return this.goToRoot(username, true);
    },
    navigateTo(username: string): Promise<void> {
      // Re-root / up / home navigation keeps the existing home root.
      return this.goToRoot(username, false);
    },
    goToUser(): void {
      const q = this.searchInput.trim();
      if (!q) return;
      // A search moves the anchor but keeps "home" (your own position) so you can
      // still go up / back to root afterwards. Make sure home is set first.
      if (!this.homeRoot && this.currentUser) {
        this.$store.commit("binaryTree/" + SET_HOME, this.currentUser);
      }
      this.navigateTo(q);
    },
    reload(): void {
      if (this.rootUsername) this.navigateTo(this.rootUsername);
    },
    goUp(levels: number): void {
      if (this.atHome) return;
      let target: string = this.rootUsername;
      for (let i = 0; i < levels; i++) {
        if (target === this.homeRoot) break;
        const parent = this.nodeFor(target)?.parent;
        if (!parent) break;
        target = (parent as string).toLowerCase();
      }
      if (target && target !== this.rootUsername) this.navigateTo(target);
    },
    goHome(): void {
      if (this.homeRoot && this.homeRoot !== this.rootUsername) {
        this.navigateTo(this.homeRoot);
      }
    },
    centerScroll(): void {
      // Reaplica el zoom primero (dimensiona el contenido con scroll) para que el
      // centrado use el scrollWidth ya correcto.
      this.applyZoom();
      const c = this.$refs.canvas as HTMLElement | undefined;
      if (c) c.scrollLeft = (c.scrollWidth - c.clientWidth) / 2;
    },
    // Usernames of the cards closest to the viewport center — persisted so we can
    // scroll back near "where the user was", independent of screen size.
    computeFocusUsernames(max: number): string[] {
      const canvas = this.$refs.canvas as HTMLElement | undefined;
      if (!canvas) return [];
      const cr = canvas.getBoundingClientRect();
      const cx = cr.left + cr.width / 2;
      const cy = cr.top + cr.height / 2;
      const scored: Array<{ u: string; d: number }> = [];
      canvas.querySelectorAll<HTMLElement>("[data-username]").forEach((el) => {
        const r = el.getBoundingClientRect();
        if (
          r.bottom < cr.top ||
          r.top > cr.bottom ||
          r.right < cr.left ||
          r.left > cr.right
        )
          return;
        const u = el.getAttribute("data-username") || "";
        if (!u) return;
        const dx = r.left + r.width / 2 - cx;
        const dy = r.top + r.height / 2 - cy;
        scored.push({ u, d: dx * dx + dy * dy });
      });
      scored.sort((a, b) => a.d - b.d);
      return scored.slice(0, max).map((s) => s.u);
    },
    restoreFocus(focusUsernames?: string[], attemptsLeft = 6): void {
      const canvas = this.$refs.canvas as HTMLElement | undefined;
      if (!canvas) return;
      const list = Array.isArray(focusUsernames) ? focusUsernames : [];
      if (list.length) {
        const byName = new Map<string, HTMLElement>();
        canvas
          .querySelectorAll<HTMLElement>("[data-username]")
          .forEach((el) => {
            const u = el.getAttribute("data-username") || "";
            if (u && !byName.has(u)) byName.set(u, el);
          });
        for (const u of list) {
          const el = byName.get((u || "").toLowerCase());
          if (el) {
            const cr = canvas.getBoundingClientRect();
            const er = el.getBoundingClientRect();
            canvas.scrollLeft +=
              er.left + er.width / 2 - (cr.left + cr.width / 2);
            canvas.scrollTop +=
              er.top + er.height / 2 - (cr.top + cr.height / 2);
            return;
          }
        }
        // Focus nodes not rendered yet (open branches still loading) — retry a
        // few times before giving up and just centering the anchor.
        if (attemptsLeft > 0) {
          window.setTimeout(
            () => this.restoreFocus(focusUsernames, attemptsLeft - 1),
            300
          );
          return;
        }
      }
      this.centerScroll();
    },
    scheduleSave(): void {
      if (this.restoring) return;
      if (this.saveTimer) window.clearTimeout(this.saveTimer);
      this.saveTimer = window.setTimeout(() => this.doSave(), SAVE_DEBOUNCE_MS);
    },
    doSave(): void {
      this.saveTimer = 0;
      const me = this.currentUser;
      if (!me || !this.rootUsername) return;
      const focusUsernames = this.computeFocusUsernames(FOCUS_COUNT);
      // Keep focus in the store too, so same-session route changes can restore
      // scroll position without re-reading the backend doc.
      this.$store.commit("binaryTree/" + SET_FOCUS, focusUsernames);
      this.$store.dispatch("binaryTree/" + saveViewState, {
        username: me,
        expandDepth: this.expandDepth,
        focusUsernames,
      });
    },
    collapseAll(): void {
      if (!this.rootUsername) return;
      this.$store.commit("binaryTree/" + CLEAR_EXPANDED);
      this.$store.dispatch("binaryTree/" + expandSubtree, {
        username: this.rootUsername,
        levels: this.initialDepth - 1,
      });
      this.$nextTick(() => this.centerScroll());
    },
    onScroll(): void {
      this.scheduleSave();
      // Cheaply track the viewport rectangle on the minimap (rAF-throttled). If
      // the content grew (a lazily-loaded branch rendered), rebuild the schematic.
      if (this.mmRaf) return;
      this.mmRaf = requestAnimationFrame(() => {
        this.mmRaf = 0;
        const canvas = this.$refs.canvas as HTMLElement | undefined;
        if (
          canvas &&
          this.minimap.show &&
          (canvas.scrollWidth !== this.mmContentW ||
            canvas.scrollHeight !== this.mmContentH)
        ) {
          this.scheduleMinimap();
        }
        this.updateMinimapViewport();
      });
    },
    // Size the canvas to fill from its top edge to near the viewport bottom, so
    // the tree scrolls inside a fixed frame (robust to the toolbar wrapping on
    // narrow screens, unlike a fixed CSS offset).
    fitCanvas(): void {
      const canvas = this.$refs.canvas as HTMLElement | undefined;
      if (!canvas) return;
      const top = canvas.getBoundingClientRect().top;
      // Leave room for the page's bottom chrome below the canvas (this view's
      // py-3 ≈16px + the app <main> my-4 ≈24px) so the frame doesn't push the
      // page into a scroll.
      const h = Math.max(380, Math.round(window.innerHeight - top - 44));
      canvas.style.height = h + "px";
    },
    // Rebuild the minimap schematic from the rendered card positions. Heavier
    // (reads every card's rect) so it's debounced via scheduleMinimap().
    computeMinimap(): void {
      const canvas = this.$refs.canvas as HTMLElement | undefined;
      const inner = this.$refs.inner as HTMLElement | undefined;
      if (!canvas || !inner) {
        this.minimap.show = false;
        return;
      }
      const contentW = canvas.scrollWidth;
      const contentH = canvas.scrollHeight;
      const overflow =
        contentW > canvas.clientWidth + 4 || contentH > canvas.clientHeight + 4;
      if (!overflow || contentW === 0 || contentH === 0) {
        this.minimap.show = false;
        return;
      }
      const scale = Math.min(MM_MAX_W / contentW, MM_MAX_H / contentH);
      const innerRect = inner.getBoundingClientRect();
      const boxes: Array<{ x: number; y: number; w: number; h: number }> = [];
      const cards = inner.querySelectorAll<HTMLElement>(".otn-card");
      const LIMIT = 1500; // safety cap so a huge tree can't stall the schematic
      let i = 0;
      cards.forEach((el) => {
        if (i++ >= LIMIT) return;
        const r = el.getBoundingClientRect();
        boxes.push({
          x: (r.left - innerRect.left) * scale,
          y: (r.top - innerRect.top) * scale,
          w: Math.max(1, r.width * scale),
          h: Math.max(1, r.height * scale),
        });
      });
      this.minimap.boxes = boxes;
      this.minimap.scale = scale;
      this.minimap.w = Math.max(1, Math.round(contentW * scale));
      this.minimap.h = Math.max(1, Math.round(contentH * scale));
      this.mmContentW = contentW;
      this.mmContentH = contentH;
      this.minimap.show = true;
      this.updateMinimapViewport();
    },
    updateMinimapViewport(): void {
      const canvas = this.$refs.canvas as HTMLElement | undefined;
      if (!canvas || !this.minimap.show) return;
      const s = this.minimap.scale;
      this.minimap.vp = {
        x: canvas.scrollLeft * s,
        y: canvas.scrollTop * s,
        w: canvas.clientWidth * s,
        h: canvas.clientHeight * s,
      };
    },
    scheduleMinimap(): void {
      if (this.mmTimer) window.clearTimeout(this.mmTimer);
      this.mmTimer = window.setTimeout(() => {
        this.mmTimer = 0;
        this.$nextTick(() =>
          requestAnimationFrame(() => this.computeMinimap())
        );
      }, MM_DEBOUNCE_MS);
    },
    // Scroll the main canvas so the point clicked/dragged on the minimap is
    // centered in the viewport.
    minimapScrollTo(offsetX: number, offsetY: number): void {
      const canvas = this.$refs.canvas as HTMLElement | undefined;
      if (!canvas || !this.minimap.scale) return;
      const s = this.minimap.scale;
      const left = offsetX / s - canvas.clientWidth / 2;
      const top = offsetY / s - canvas.clientHeight / 2;
      canvas.scrollLeft = Math.max(
        0,
        Math.min(left, canvas.scrollWidth - canvas.clientWidth)
      );
      canvas.scrollTop = Math.max(
        0,
        Math.min(top, canvas.scrollHeight - canvas.clientHeight)
      );
    },
    minimapPointerDown(e: PointerEvent): void {
      this.mmDragging = true;
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
      this.minimapScrollTo(e.offsetX, e.offsetY);
    },
    minimapPointerMove(e: PointerEvent): void {
      if (this.mmDragging) this.minimapScrollTo(e.offsetX, e.offsetY);
    },
    minimapPointerUp(e: PointerEvent): void {
      this.mmDragging = false;
      const el = e.currentTarget as Element;
      if (el.hasPointerCapture(e.pointerId))
        el.releasePointerCapture(e.pointerId);
    },
    // Reposicionar el minimapa arrastrando la manija (mouse/touch). stopPropagation
    // para no disparar la navegación del SVG que hay debajo.
    mmDragStart(e: PointerEvent): void {
      e.preventDefault();
      e.stopPropagation();
      const grip = e.currentTarget as HTMLElement;
      const mm = grip.closest(".tp-minimap") as HTMLElement | null;
      const stage = mm?.parentElement as HTMLElement | null;
      if (!mm || !stage) return;
      const mmr = mm.getBoundingClientRect();
      const sr = stage.getBoundingClientRect();
      this.mmMoveActive = true;
      this.mmMovePointerId = e.pointerId;
      this.mmMoveOffX = e.clientX - mmr.left;
      this.mmMoveOffY = e.clientY - mmr.top;
      this.mmMoveStageL = sr.left;
      this.mmMoveStageT = sr.top;
      this.mmMoveStageW = sr.width;
      this.mmMoveStageH = sr.height;
      this.mmMoveW = mmr.width;
      this.mmMoveH = mmr.height;
      try {
        grip.setPointerCapture(e.pointerId);
      } catch (err) {
        // captura best-effort
      }
    },
    mmDragMove(e: PointerEvent): void {
      if (!this.mmMoveActive || e.pointerId !== this.mmMovePointerId) return;
      let left = e.clientX - this.mmMoveStageL - this.mmMoveOffX;
      let top = e.clientY - this.mmMoveStageT - this.mmMoveOffY;
      // Mantener el minimapa dentro del escenario.
      left = Math.max(0, Math.min(left, this.mmMoveStageW - this.mmMoveW));
      top = Math.max(0, Math.min(top, this.mmMoveStageH - this.mmMoveH));
      this.mmPosLeft = left;
      this.mmPosTop = top;
    },
    mmDragEnd(e: PointerEvent): void {
      if (e.pointerId !== this.mmMovePointerId) return;
      this.mmMoveActive = false;
      this.mmMovePointerId = null;
      const grip = e.currentTarget as HTMLElement;
      if (grip.hasPointerCapture && grip.hasPointerCapture(e.pointerId)) {
        grip.releasePointerCapture(e.pointerId);
      }
    },
    onResize(): void {
      this.fitCanvas();
      this.scheduleMinimap();
    },
    isFresh(saved: BinaryTreeViewStatus): boolean {
      return (
        typeof saved.savedAt === "number" &&
        Date.now() - saved.savedAt < STALE_MS
      );
    },
    applySavedState(saved: BinaryTreeViewStatus): void {
      // Cold restore only: keep the nodes nearest the anchor up to the cap, but
      // ALWAYS keep the focus column (anchor→where the user was) so we land back
      // exactly there; off-view side branches stay collapsed and reopen on
      // scroll. (Same-session screen switches don't pass through here — they
      // restore the full in-memory state.)
      const openAll = Array.isArray(saved.openNodes) ? saved.openNodes : [];
      const path = (Array.isArray(saved.focusPath) ? saved.focusPath : [])
        .map((u) => (u || "").toLowerCase())
        .filter(Boolean);
      const keep = new Set<string>();
      openAll
        .slice(0, MAX_RESTORED_OPEN)
        .forEach((u) => keep.add((u || "").toLowerCase()));
      path.forEach((u) => keep.add(u));
      const openNodes = Array.from(keep);
      if (openAll.length > openNodes.length) {
        this.notice =
          `Vista muy grande: se restauraron ${openNodes.length} de ` +
          `${openAll.length} nodos abiertos (priorizando tu posición). ` +
          `Las ramas fuera de vista se reabren al hacer scroll.`;
      }
      if (typeof saved.expandDepth === "number" && saved.expandDepth >= 1) {
        this.expandDepth = saved.expandDepth;
      }
      this.$store.commit("binaryTree/" + APPLY_VIEW_STATE, {
        homeUsername: saved.homeUsername || saved.anchorUsername,
        anchorUsername: saved.anchorUsername,
        openNodes,
        focusUsernames: Array.isArray(saved.focusUsernames)
          ? saved.focusUsernames
          : [],
      });
    },
  },
  watch: {
    storeRevision(): void {
      this.scheduleSave();
      this.scheduleMinimap();
      // El árbol pudo crecer/encogerse (ramas cargadas o colapsadas): reajusta
      // el tamaño escalado del contenido con scroll.
      this.$nextTick(() => this.applyZoom());
    },
    zoom(): void {
      this.$nextTick(() => {
        this.applyZoom();
        this.scheduleMinimap();
      });
    },
    expandDepth(): void {
      this.scheduleSave();
    },
    notice(): void {
      // The notice banner shifts the canvas down — refit so it still fills the
      // viewport without producing page scroll.
      this.$nextTick(() => this.fitCanvas());
    },
  },
  async mounted(): Promise<void> {
    const canvas = this.$refs.canvas as HTMLElement | undefined;
    this.cardObserver.init(canvas as Element);
    if (canvas) {
      canvas.addEventListener("scroll", this.onScroll, { passive: true });
      canvas.addEventListener("pointerdown", this.onCanvasPointerDown);
      canvas.addEventListener("pointermove", this.onCanvasPointerMove);
      canvas.addEventListener("pointerup", this.onCanvasPointerUp);
      canvas.addEventListener("pointercancel", this.onCanvasPointerUp);
      // Captura para poder tragar el clic tras un arrastre (antes de que llegue al nodo).
      canvas.addEventListener("click", this.onCanvasClickCapture, true);
    }
    window.addEventListener("resize", this.onResize);
    this.fitCanvas();
    this.errorModalInst = new bs.Modal(
      document.getElementById("error-modal") as HTMLElement
    );

    this.restoring = true;
    try {
      const me = this.currentUser;
      if (this.treeRoot) {
        // Same-session return: the store still holds the last viewport and the
        // focus, so restore both (re-center where the user was).
        const focus = this.storeFocus;
        await this.navigateTo(this.treeRoot);
        this.$nextTick(() =>
          requestAnimationFrame(() => this.restoreFocus(focus))
        );
        return;
      }
      const saved: BinaryTreeViewStatus | null = me
        ? await this.$store.dispatch("binaryTree/" + loadViewState, me)
        : null;
      if (saved && saved.anchorUsername && this.isFresh(saved)) {
        this.applySavedState(saved);
        // Prefetch the focus column in one batch (text only) so a deep restore
        // renders straight down to where the user was instead of cascading many
        // sequential single-node fetches. Images stay lazy.
        const path = Array.isArray(saved.focusPath) ? saved.focusPath : [];
        if (path.length > this.initialDepth) {
          try {
            await this.$store.dispatch("binaryTree/" + loadNodesBatch, {
              usernames: path,
              levels: 2,
            });
          } catch (e) {
            // fall back to the per-node cascade if the batch fails
            // eslint-disable-next-line no-console
            console.error("binaryTree: prefetch de columna falló", e);
          }
        }
        await this.goToRoot(saved.anchorUsername, false);
        if (!this.rootReady && me) {
          await this.load(me);
        } else {
          this.$nextTick(() =>
            requestAnimationFrame(() => this.restoreFocus(saved.focusUsernames))
          );
        }
      } else if (saved && saved.anchorUsername) {
        // Stale: keep the home, but re-seed a fresh shallow expansion.
        this.$store.commit(
          "binaryTree/" + SET_HOME,
          saved.homeUsername || saved.anchorUsername
        );
        await this.goToRoot(saved.anchorUsername, false);
        if (!this.rootReady && me) await this.load(me);
      } else if (me) {
        await this.load(me);
      } else {
        // Sin usuario y sin nada que restaurar: no hay carga que corra, así que
        // apaga el loading para no dejar el skeleton colgado para siempre.
        this.loading = false;
      }
    } finally {
      this.restoring = false;
    }
  },
  beforeUnmount(): void {
    const canvas = this.$refs.canvas as HTMLElement | undefined;
    if (canvas) {
      canvas.removeEventListener("scroll", this.onScroll);
      canvas.removeEventListener("pointerdown", this.onCanvasPointerDown);
      canvas.removeEventListener("pointermove", this.onCanvasPointerMove);
      canvas.removeEventListener("pointerup", this.onCanvasPointerUp);
      canvas.removeEventListener("pointercancel", this.onCanvasPointerUp);
      canvas.removeEventListener("click", this.onCanvasClickCapture, true);
    }
    window.removeEventListener("resize", this.onResize);
    if (this.saveTimer) window.clearTimeout(this.saveTimer);
    if (this.mmTimer) window.clearTimeout(this.mmTimer);
    if (this.mmRaf) cancelAnimationFrame(this.mmRaf);
    // Best-effort flush so the latest state is persisted on the way out.
    this.doSave();
    this.cardObserver.destroy();
  },
  setup() {
    const cardObserver = createCardObserver();
    provide("binaryTreeCardObserver", {
      register: cardObserver.register,
      unregister: cardObserver.unregister,
    });
    return { cardObserver };
  },
});
